import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Globals } from 'src/app/Globals';
import { HttpService } from 'src/app/services/http-service';
import { getSignupUserId } from '../signup-user';

@Component({
  selector: 'app-reg-driver-payout-setup',
  templateUrl: './reg-driver-payout-setup.component.html',
  styleUrls: ['./reg-driver-payout-setup.component.scss']
})
export class RegDriverPayoutSetupComponent implements OnInit {
  globals = Globals;
  isLoadingResults = false;
  inviteEmailed = false;
  onboardingLink: string = '';
  profileDetails: any = {};

  constructor(
    private service: HttpService,
    private router: Router
  ) {}

  ngOnInit() {
    const merchantId = getSignupUserId();
    if (!merchantId) {
      this.service.showError('Please complete registration first.', 'ERROR');
      this.router.navigate(['/registration/registration-details']);
      return;
    }

    this.profileDetails = JSON.parse(localStorage.getItem('driverProfile') || '{}');
    if (!this.profileDetails.email || !this.profileDetails.licenseFrontImage) {
      this.service.showError('Please complete driver profile and verification first.', 'ERROR');
      this.router.navigate(['/registration/drivers/profile-details']);
      return;
    }

    if (this.profileDetails.onboardingLink) {
      this.onboardingLink = this.profileDetails.onboardingLink;
    }
    if (this.profileDetails.inviteEmailed) {
      this.inviteEmailed = true;
    }

    this.ensureDriverThen(() => {
      this.markPayoutStepDone();
    });
  }

  private saveProfile() {
    localStorage.setItem('driverProfile', JSON.stringify(this.profileDetails));
  }

  private markPayoutStepDone() {
    try {
      localStorage.setItem('reg-driver-payout-submitted', '1');
    } catch (e) { /* ignore */ }
  }

  private errText(err: any, fallback: string): string {
    if (!err) {
      return fallback;
    }
    if (typeof err === 'string') {
      return err;
    }
    if (typeof err.message === 'string' && err.message) {
      return err.message;
    }
    return fallback;
  }

  private applyInviteResult(data: any, message?: string, showToast: boolean = true) {
    if (!data) {
      return;
    }
    this.inviteEmailed = !!data.inviteEmailed;
    if (data.onboardingLink || data.url) {
      this.onboardingLink = data.onboardingLink || data.url;
      this.profileDetails.onboardingLink = this.onboardingLink;
    }
    this.profileDetails.inviteEmailed = this.inviteEmailed;
    this.saveProfile();

    if (!showToast) {
      return;
    }
    // Email failure with a link is expected (SendGrid credits) — page copy handles it; no red toast.
    if (this.inviteEmailed) {
      this.service.showSuccess(
        (typeof message === 'string' && message) || 'Payout setup invite emailed to the driver',
        'SUCCESS'
      );
    }
  }

  private buildCreateParams(merchantId: string) {
    return {
      merchantId,
      _id: merchantId,
      profileImage: this.profileDetails.profileImage,
      email: this.profileDetails.email,
      phoneNumberPrefix: this.profileDetails.phoneNumberPrefix,
      phoneNumber: this.profileDetails.phoneNumber,
      firstName: this.profileDetails.firstName,
      lastName: this.profileDetails.lastName,
      vehicleId: this.profileDetails.vehicleId,
      licenseFrontImage: this.profileDetails.licenseFrontImage,
      identityCardFrontImage: this.profileDetails.identityCardFrontImage,
      licenseExpiry: this.profileDetails.licenseExpiry,
      lineAddress: this.profileDetails.lineAddress,
      city: this.profileDetails.city,
      country: this.profileDetails.country || 'US',
      postalCode: this.profileDetails.postalCode,
      state: this.profileDetails.state,
      rental: this.profileDetails.rental,
      monthlyRentalAmount: this.profileDetails.monthlyRentalAmount,
      dob: this.profileDetails.dob
    };
  }

  ensureDriverThen(done: () => void) {
    const merchantId = getSignupUserId();
    if (this.profileDetails.driverId) {
      done();
      return;
    }

    this.isLoadingResults = true;
    this.service.postRequest(
      this.globals.urls.signUp.noauth.addDriver,
      this.buildCreateParams(merchantId)
    ).subscribe(
      res => {
        this.isLoadingResults = false;
        if (res.success === 1 && res.data && res.data.driverId) {
          this.profileDetails.driverId = res.data.driverId;
          this.profileDetails.driverCreated = true;
          this.applyInviteResult(res.data, res.message, true);
          done();
        } else {
          this.service.showError(this.errText(res && res.message, 'Could not create driver'), 'ERROR');
        }
      },
      err => {
        this.isLoadingResults = false;
        this.service.showError(this.errText(err, 'Could not create driver'), 'ERROR');
      }
    );
  }

  resendInvite() {
    const merchantId = getSignupUserId();
    const driverId = this.profileDetails.driverId;
    if (!merchantId || !driverId) {
      return;
    }
    this.isLoadingResults = true;
    this.service.postRequest(this.globals.urls.signUp.noauth.driverConnectOnboardingLink, {
      merchantId,
      _id: merchantId,
      driverId
    }).subscribe(
      res => {
        this.isLoadingResults = false;
        if (res.success === 1 && res.data) {
          this.applyInviteResult(res.data, res.message, true);
          if (!this.inviteEmailed && this.onboardingLink) {
            this.service.showSuccess('New payout link ready — copy and share with the driver', 'SUCCESS');
          }
        } else {
          this.service.showError(this.errText(res && res.message, 'Could not resend invite'), 'ERROR');
        }
      },
      err => {
        this.isLoadingResults = false;
        this.service.showError(this.errText(err, 'Could not resend invite'), 'ERROR');
      }
    );
  }

  copyLink() {
    if (!this.onboardingLink) {
      return;
    }
    if (navigator && (navigator as any).clipboard && (navigator as any).clipboard.writeText) {
      (navigator as any).clipboard.writeText(this.onboardingLink).then(() => {
        this.service.showSuccess('Payout link copied', 'SUCCESS');
      }).catch(() => {
        this.service.showError('Could not copy link', 'ERROR');
      });
      return;
    }
    window.prompt('Copy this payout link', this.onboardingLink);
  }

  continueToPreview() {
    if (!this.profileDetails.driverId) {
      this.service.showError('Please wait until the driver is created.', 'ERROR');
      return;
    }
    this.markPayoutStepDone();
    this.router.navigate(['/registration/drivers/preview']);
  }

  back() {
    this.router.navigate(['/registration/drivers/verification']);
  }
}
